import React, { useEffect, useState, useCallback } from "react";
import { format } from "date-fns";
import {
  Loader2,
  AlertCircle,
  Search,
  Eye,
  FileText,
  X,
  Calendar,
  Clock,
  Users,
  CheckCircle2,
  RefreshCcw,
  Download,
  Mail,
  Wallet,
  ClipboardCheck,
  Phone,
  IdCard,
  Trash2,
  Pencil,
} from "lucide-react";

import EditBookingModal from "../../components/admin/EditBookingModel";

import {
  getAllBookingsAdmin,
  getBookingById,
  updateBookingStatus,
  updatePaymentStatus, // NEW - make sure this exists in bookingService (mirrors updateBookingStatus)
  buildInvoiceFromBooking,
  deleteBooking, // NEW
  getRefundInfo,
  setPassIssuedAt,
} from "../../services/bookingService"; // adjust path to match your project

import { getEmailTemplate } from "../../services/emailService"; // adjust path
import { getFunctions, httpsCallable } from "firebase/functions";

import { generateGolfPass } from "../../utils/generateGolfPass";
import { getGolferRoster } from "../../utils/golferRoster";
import exquraLogo from "../../assets/branding/exqura-wordmark.png"; // adjust to your actual logo file

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "pending_payment", label: "Pending Payment" },
  { value: "confirmed", label: "Confirmed" },
  { value: "cancelled", label: "Cancelled" },
];

const PAGE_SIZE = 10;

export default function AdminManageBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const [selectedBooking, setSelectedBooking] = useState(null);
  // const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const [invoiceBooking, setInvoiceBooking] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState(null);
  const [generatingPass, setGeneratingPass] = useState(false);
  const [passError, setPassError] = useState(null);

  // Email-the-pass state - mirrors the download state above.
  const [emailingPass, setEmailingPass] = useState(false);
  const [emailError, setEmailError] = useState(null);
  // Tracks the booking's itineraryEmailSentAt so the modal can show
  // "Last sent: ..." and admins don't double-send by accident.
  const [emailSentAt, setEmailSentAt] = useState(null);

  // View-the-pass (open PDF in a new tab, no download) state.
  const [viewingPass, setViewingPass] = useState(false);

  // Mark-as-paid / confirm-booking popup - holds the booking currently
  // being actioned, and its own loading + error state.
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState(null);

  // Delete-booking popup state.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteRefreshing, setDeleteRefreshing] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const openEdit = async (booking) => {
    setEditTarget(booking);
    try {
      const fresh = await getBookingById(booking.id);
      if (fresh) setEditTarget(fresh);
    } catch (err) {
      console.error("Failed to refresh booking before edit:", err);
    }
  };

  const loadBookings = useCallback(
    async (opts = {}) => {
      const { append = false } = opts;
      append ? setLoadingMore(true) : setLoading(true);
      setError(null);
      try {
        const {
          bookings: page,
          nextCursor,
          hasMore: more,
        } = await getAllBookingsAdmin({
          pageSize: PAGE_SIZE,
          cursor: append ? cursor : null,
          status: statusFilter,
        });
        setBookings((prev) => (append ? [...prev, ...page] : page));
        setCursor(nextCursor);
        setHasMore(more);
      } catch (err) {
        console.error("Failed to load bookings:", err);
        setError(err?.message || "Failed to load bookings. Please try again.");
      } finally {
        append ? setLoadingMore(false) : setLoading(false);
      }
    },
    [cursor, statusFilter],
  );

  // Reload from scratch whenever the status filter changes.
  useEffect(() => {
    setCursor(null);
    loadBookings({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const filteredBookings = bookings.filter((b) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.trim().toLowerCase();
    return (
      b.id.toLowerCase().includes(term) ||
      (b.customerName || "").toLowerCase().includes(term) ||
      (b.customerEmail || "").toLowerCase().includes(term) ||
      (b.destination || "").toLowerCase().includes(term)
    );
  });

  // Generic helper: patch a booking's fields everywhere it's held in
  // state (list, detail modal, confirm popup) so all views stay in sync
  // without a refetch.
  const patchBookingEverywhere = (bookingId, patch) => {
    setBookings((prev) =>
      prev.map((b) => (b.id === bookingId ? { ...b, ...patch } : b)),
    );
    setSelectedBooking((prev) =>
      prev?.id === bookingId ? { ...prev, ...patch } : prev,
    );
    setConfirmAction((prev) =>
      prev?.id === bookingId ? { ...prev, ...patch } : prev,
    );
  };

  // const handleStatusChange = async (booking, newStatus) => {
  //   setUpdatingStatusId(booking.id);
  //   try {
  //     await updateBookingStatus(booking.id, newStatus);
  //     patchBookingEverywhere(booking.id, { status: newStatus });
  //   } catch (err) {
  //     console.error("Failed to update booking status:", err);
  //     alert(err?.message || "Failed to update status. Please try again.");
  //   } finally {
  //     setUpdatingStatusId(null);
  //   }
  // };

  // Opens the "Mark as Paid / Confirm Booking" popup for a given booking.
  const openConfirmAction = (booking) => {
    setConfirmError(null);
    setConfirmAction(booking);
  };

  const closeConfirmAction = () => {
    setConfirmAction(null);
    setConfirmError(null);
  };

  // Sets paymentStatus to "paid" - separate field from booking status,
  // so a booking can be paid but not yet confirmed, or vice versa.
  const handleMarkPaid = async (booking) => {
    setConfirmLoading(true);
    setConfirmError(null);
    try {
      await updatePaymentStatus(booking.id, "paid");
      patchBookingEverywhere(booking.id, { paymentStatus: "paid" });
    } catch (err) {
      console.error("Failed to mark booking as paid:", err);
      setConfirmError(
        err?.message || "Failed to update payment status. Please try again.",
      );
    } finally {
      setConfirmLoading(false);
    }
  };

  // Sets booking status to "confirmed".
  const handleConfirmBooking = async (booking) => {
    setConfirmLoading(true);
    setConfirmError(null);
    try {
      await updateBookingStatus(booking.id, "confirmed");
      patchBookingEverywhere(booking.id, { status: "confirmed" });
    } catch (err) {
      console.error("Failed to confirm booking:", err);
      setConfirmError(
        err?.message || "Failed to confirm booking. Please try again.",
      );
    } finally {
      setConfirmLoading(false);
    }
  };

  // Opens the delete popup, then re-reads the booking so the refund amount
  // shown is based on live data, not a possibly stale list row.
  const openDelete = async (booking) => {
    setDeleteError(null);
    setDeleteTarget(booking);
    setDeleteRefreshing(true);
    try {
      const fresh = await getBookingById(booking.id);
      if (fresh) {
        setDeleteTarget((prev) => (prev?.id === booking.id ? fresh : prev));
      } else {
        setBookings((prev) => prev.filter((b) => b.id !== booking.id));
        setDeleteError("This booking no longer exists.");
      }
    } catch (err) {
      console.error("Failed to refresh booking before delete:", err);
      setDeleteError(err?.message || "Could not load the latest booking data.");
    } finally {
      setDeleteRefreshing(false);
    }
  };

  const closeDelete = () => {
    if (deleteLoading) return;
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const handleDeleteBooking = async () => {
    if (!deleteTarget) return;
    const { paid } = getRefundInfo(deleteTarget);
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      // The popup is the confirmation, so force is safe for paid bookings.
      // If the booking became paid after the popup opened, force is false
      // and the service refuses, so you never delete without the warning.
      await deleteBooking(deleteTarget.id, { force: paid });

      const id = deleteTarget.id;
      setBookings((prev) => prev.filter((b) => b.id !== id));
      setSelectedBooking((prev) => (prev?.id === id ? null : prev));
      setConfirmAction((prev) => (prev?.id === id ? null : prev));
      if (invoiceBooking?.id === id) {
        setInvoiceBooking(null);
        setInvoice(null);
      }
      setDeleteTarget(null);
    } catch (err) {
      console.error("Failed to delete booking:", err);
      setDeleteError(
        err?.message || "Failed to delete booking. Please try again.",
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDetail = async (booking) => {
    setSelectedBooking(booking);
    // Refresh with full doc in case list data is stale/partial.
    const fresh = await getBookingById(booking.id);
    if (fresh) setSelectedBooking(fresh);
  };

  const openInvoice = (booking) => {
    setInvoiceBooking(booking);
    setInvoiceError(null);
    setPassError(null);
    setEmailError(null);
    setEmailSentAt(booking.itineraryEmailSentAt || null);
    try {
      const inv = buildInvoiceFromBooking(booking);
      setInvoice(inv);
    } catch (err) {
      console.error("Failed to build invoice:", err);
      setInvoiceError(err?.message || "Failed to generate invoice.");
    }
  };

  const buildPassInput = async () => {
    if (!invoiceBooking) return invoice;
    const fresh = (await getBookingById(invoiceBooking.id)) || invoiceBooking;
    return {
      ...invoice,
      ...fresh,
      roster: getGolferRoster(fresh),
      // Passes emailed before passIssuedAt existed only have the last send
      // time — use it so a re-send doesn't stamp today's date.
      passIssuedAt: fresh.passIssuedAt || fresh.itineraryEmailSentAt || null,
    };
  };

  // Pulls the photos + instruction text the admin saved on the
  // "Itinerary + Golf Getaway Pass" email template and bakes them into the
  // downloadable PDF, so the pass always matches what customers were told.
  const handleDownloadPass = async () => {
    if (!invoice) return;
    setPassError(null);
    setGeneratingPass(true);
    try {
      const template = await getEmailTemplate("itinerary_pass");
      const passInput = await buildPassInput();
      await generateGolfPass(
        passInput, // <-- was `invoice`
        template?.images,
        template?.instruction,
        exquraLogo,
      );
    } catch (err) {
      console.error("Failed to generate Golf Getaway Pass:", err);
      setPassError(err?.message || "Failed to generate the PDF pass.");
    } finally {
      setGeneratingPass(false);
    }
  };

  // Builds the same PDF but opens it in a new browser tab instead of saving a
  // file — so an admin can eyeball the voucher straight from the modal.
  const handleViewPass = async () => {
    if (!invoice) return;
    setPassError(null);
    setViewingPass(true);
    // Open the tab synchronously inside the click so the browser doesn't treat
    // the post-await navigation as a blocked popup.
    const tab = window.open("", "_blank");
    try {
      const template = await getEmailTemplate("itinerary_pass");
      const passInput = await buildPassInput();
      const url = await generateGolfPass(
        passInput,
        template?.images,
        template?.instruction,
        exquraLogo,
        undefined, // timezoneNote (unused)
        { returnBlobUrl: true },
      );
      if (tab) tab.location = url;
      else window.open(url, "_blank");
      // Release the blob once the tab has had time to load it.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (tab) tab.close();
      console.error("Failed to open Golf Getaway Pass:", err);
      setPassError(err?.message || "Failed to open the PDF pass.");
    } finally {
      setViewingPass(false);
    }
  };

  // Builds the same PDF (as base64 this time, no download) and hands it to
  // the sendItineraryEmail cloud function, which emails it to the customer
  // on file and stamps itineraryEmailSentAt on the booking doc.
  const handleEmailPass = async () => {
    if (!invoice || !invoiceBooking) return;
    setEmailError(null);
    setEmailingPass(true);
    try {
      const template = await getEmailTemplate("itinerary_pass");
      const passInput = await buildPassInput();
      // First send fixes the pass issue date; re-sends reuse it.
      if (!passInput.passIssuedAt) passInput.passIssuedAt = new Date().toISOString();
      const pdfBase64 = await generateGolfPass(
        passInput,
        template?.images,
        template?.instruction,
        exquraLogo,
        undefined,
        { returnBase64: true },
      );

      const functions = getFunctions();
      const sendItineraryEmail = httpsCallable(functions, "sendItineraryEmail");
      await sendItineraryEmail({
        bookingId: invoiceBooking.id,
        pdfBase64,
      });

      const sentAt = new Date().toISOString();
      setEmailSentAt(sentAt);
      // Re-writes the same date on re-sends; locks in the fallback date for
      // passes first sent before passIssuedAt existed.
      await setPassIssuedAt(invoiceBooking.id, passInput.passIssuedAt);
      patchBookingEverywhere(invoiceBooking.id, {
        itineraryEmailSentAt: sentAt,
        passIssuedAt: passInput.passIssuedAt,
      });
    } catch (err) {
      console.error("Failed to email Golf Getaway Pass:", err);
      setEmailError(err?.message || "Failed to send the email.");
    } finally {
      setEmailingPass(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-[#0B2421]">
              Manage Bookings
            </h1>
            <p className="text-gray-500 mt-1">
              View, update, and invoice customer bookings.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setCursor(null);
              loadBookings({ append: false });
            }}
            className="h-11 px-4 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-100 transition-colors flex items-center gap-2"
          >
            <RefreshCcw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by booking ID, customer, or destination"
              className="w-full h-11 pl-10 pr-4 rounded-xl border border-gray-200 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setStatusFilter(opt.value)}
                className={`h-11 px-4 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${
                  statusFilter === opt.value
                    ? "bg-[#0B2421] text-white"
                    : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mb-6 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400 gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading bookings...
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              No bookings found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
                    <th className="px-5 py-3.5">Customer</th>
                    <th className="px-5 py-3.5">Package</th>
                    <th className="px-5 py-3.5">Players</th>
                    <th className="px-5 py-3.5">Reservation No.</th>
                    <th className="px-5 py-3.5">Total</th>
                    <th className="px-5 py-3.5">Payment</th>
                    <th className="px-5 py-3.5">Status</th>
                    <th className="px-5 py-3.5">Booked On</th>
                    <th className="px-5 py-3.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking) => (
                    <tr
                      key={booking.id}
                      className="border-b border-gray-50 last:border-0 hover:bg-gray-50/70 transition-colors"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <Avatar name={booking.customerName} />
                          <div className="min-w-0">
                            <p className="font-semibold text-[#0B2421] text-sm truncate">
                              {booking.customerName || "Guest"}
                            </p>
                            <p className="text-xs text-gray-400 truncate">
                              {booking.customerEmail || "-"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-gray-700 font-medium text-sm">
                          {booking.selectedPackage?.rounds
                            ? `${booking.selectedPackage.rounds} Round${
                                booking.selectedPackage.rounds > 1 ? "s" : ""
                              }`
                            : "-"}
                        </p>
                        <p className="text-xs text-gray-400">
                          {booking.selectedCourses?.[0]?.city
                            ? `${booking.selectedCourses[0].city}, ${booking.selectedCourses[0].country || ""}`
                            : ""}
                        </p>
                      </td>
                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-1 text-gray-600">
                          <Users className="h-3.5 w-3.5 text-gray-400" />
                          {booking.numPlayers ?? "-"}
                        </span>
                      </td>
                      <td className="px-5 py-4 font-mono text-xs text-gray-600">
                        {booking.reservationNumber || "-"}
                      </td>
                      <td className="px-5 py-4 font-semibold text-[#0B2421]">
                        {booking.grandTotal != null
                          ? `SGD ${booking.grandTotal}`
                          : "-"}
                      </td>
                      <td className="px-5 py-4">
                        <PaymentBadge paymentStatus={booking.paymentStatus} />
                      </td>
                      <td className="px-5 py-4">
                        <StatusBadge status={booking.status} />
                      </td>
                      <td className="px-5 py-4 text-gray-500 text-xs">
                        <div>{formatTimestamp(booking.createdAtServer)}</div>
                        <div className="text-gray-400">
                          {formatTimeOnly(booking.createdAtServer)}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-1.5">
                          <IconButton
                            onClick={() => openDetail(booking)}
                            title="View details"
                          >
                            <Eye className="h-4 w-4" />
                          </IconButton>
                          <IconButton
                            onClick={() => openInvoice(booking)}
                            title="Invoice"
                          >
                            <FileText className="h-4 w-4" />
                          </IconButton>
                          {(booking.paymentStatus !== "paid" ||
                            booking.status !== "confirmed") && (
                            <button
                              type="button"
                              onClick={() => openConfirmAction(booking)}
                              className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center gap-1.5 transition-colors"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              Manage
                            </button>
                          )}

                          {/* <IconButton
                            onClick={() => openDelete(booking)}
                            title="Delete booking"
                            variant="danger"
                          >
                            <Trash2 className="h-4 w-4" />
                          </IconButton> */}

                          <IconButton
                            onClick={() => openEdit(booking)}
                            title="Edit booking"
                          >
                            <Pencil className="h-4 w-4" />
                          </IconButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && hasMore && (
          <div className="flex justify-center mt-6">
            <button
              type="button"
              onClick={() => loadBookings({ append: true })}
              disabled={loadingMore}
              className="h-11 px-6 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-100 disabled:opacity-40 flex items-center gap-2"
            >
              {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
              Load More
            </button>
          </div>
        )}
      </div>

      {selectedBooking && (
        <BookingDetailModal
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
        />
      )}

      {invoiceBooking && (
        <InvoiceModal
          loading={invoiceLoading}
          error={invoiceError}
          invoice={invoice}
          onViewPass={handleViewPass}
          viewingPass={viewingPass}
          onDownloadPass={handleDownloadPass}
          generatingPass={generatingPass}
          passError={passError}
          onEmailPass={handleEmailPass}
          emailingPass={emailingPass}
          emailError={emailError}
          emailSentAt={emailSentAt}
          onClose={() => {
            setInvoiceBooking(null);
            setInvoice(null);
            setInvoiceError(null);
            setPassError(null);
            setEmailError(null);
            setEmailSentAt(null);
          }}
        />
      )}

      {confirmAction && (
        <ConfirmActionModal
          booking={confirmAction}
          loading={confirmLoading}
          onEdit={() => {
            const b = confirmAction;
            closeConfirmAction();
            openEdit(b);
          }}
          error={confirmError}
          onMarkPaid={() => handleMarkPaid(confirmAction)}
          onConfirmBooking={() => handleConfirmBooking(confirmAction)}
          onClose={closeConfirmAction}
        />
      )}

      {deleteTarget && (
        <DeleteBookingModal
          booking={deleteTarget}
          refreshing={deleteRefreshing}
          loading={deleteLoading}
          error={deleteError}
          onConfirm={handleDeleteBooking}
          onClose={closeDelete}
        />
      )}

      {editTarget && (
        <EditBookingModal
          booking={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={(id, patch) => {
            patchBookingEverywhere(id, patch);
            setEditTarget(null);
          }}
          onDelete={() => {
            const b = editTarget;
            setEditTarget(null);
            openDelete(b);
          }}
        />
      )}
    </div>
  );
}

/* ---------- status / payment badges ---------- */

function StatusBadge({ status }) {
  const config = {
    confirmed: {
      dot: "bg-emerald-500",
      text: "text-emerald-700",
      bg: "bg-emerald-50",
    },
    pending_payment: {
      dot: "bg-amber-500",
      text: "text-amber-700",
      bg: "bg-amber-50",
    },
    cancelled: { dot: "bg-red-500", text: "text-red-700", bg: "bg-red-50" },
  };
  const label = status || "pending_payment";
  const c = config[label] || {
    dot: "bg-gray-400",
    text: "text-gray-600",
    bg: "bg-gray-50",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
      {label.replace("_", " ")}
    </span>
  );
}

function PaymentBadge({ paymentStatus }) {
  const config = {
    paid: {
      dot: "bg-emerald-500",
      text: "text-emerald-700",
      bg: "bg-emerald-50",
      label: "paid",
    },
    unpaid: {
      dot: "bg-amber-500",
      text: "text-amber-700",
      bg: "bg-amber-50",
      label: "unpaid",
    },
  };
  const key = paymentStatus === "paid" ? "paid" : "unpaid";
  const c = config[key];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
}

function Avatar({ name }) {
  const initials = (name || "?")
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div className="h-9 w-9 rounded-full bg-[#0B2421] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
      {initials}
    </div>
  );
}

function IconButton({
  children,
  onClick,
  title,
  variant = "default",
  disabled,
}) {
  const variants = {
    default: "border-gray-200 text-gray-500 hover:bg-gray-100",
    danger: "border-red-200 text-red-600 hover:bg-red-50",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`h-9 w-9 rounded-lg border flex items-center justify-center disabled:opacity-40 transition-colors ${variants[variant]}`}
    >
      {disabled ? <Loader2 className="h-4 w-4 animate-spin" /> : children}
    </button>
  );
}

/* ---------- mark as paid / confirm booking popup ---------- */

function ConfirmActionModal({
  booking,
  loading,
  error,
  onMarkPaid,
  onEdit,
  onConfirmBooking,
  onClose,
}) {
  const isPaid = booking.paymentStatus === "paid";
  const isConfirmed = booking.status === "confirmed";
  const isDone = isPaid && isConfirmed;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h3 className="text-lg font-bold text-[#0B2421]">Manage Booking</h3>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
            <Avatar name={booking.customerName} />
            <div className="min-w-0">
              <p className="font-semibold text-[#0B2421] text-sm truncate">
                {booking.customerName || "Guest"}
              </p>
              <p className="text-xs text-gray-400 truncate">
                {booking.reservationNumber || booking.id}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <PaymentBadge paymentStatus={booking.paymentStatus} />
            <StatusBadge status={booking.status} />
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {isDone ? (
            <p className="text-sm text-gray-500 text-center py-2">
              This booking is already paid and confirmed.
            </p>
          ) : (
            <div className="space-y-2.5">
              {!isPaid && (
                <button
                  type="button"
                  onClick={onMarkPaid}
                  disabled={loading}
                  className="w-full h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Wallet className="h-4 w-4" />
                  )}
                  Mark as Paid
                </button>
              )}
              {!isConfirmed && (
                <button
                  type="button"
                  onClick={onConfirmBooking}
                  disabled={loading}
                  className="w-full h-11 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ClipboardCheck className="h-4 w-4" />
                  )}
                  Confirm Booking
                </button>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={onEdit}
            className="w-full h-11 rounded-xl border border-[#0B2421] text-[#0B2421] font-semibold hover:bg-[#0B2421]/5 flex items-center justify-center gap-2"
          >
            <Pencil className="h-4 w-4" /> Edit Booking
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- delete booking popup ---------- */

function DeleteBookingModal({
  booking,
  refreshing,
  loading,
  error,
  onConfirm,
  onClose,
}) {
  const { paid, amount } = getRefundInfo(booking);
  const amountLabel = `SGD ${Number(amount).toFixed(2)}`;
  const busy = loading || refreshing;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h3 className="text-lg font-bold text-[#0B2421]">Delete Booking</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="h-8 w-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
            <Avatar name={booking.customerName} />
            <div className="min-w-0">
              <p className="font-semibold text-[#0B2421] text-sm truncate">
                {booking.customerName || "Guest"}
              </p>
              <p className="text-xs text-gray-400 truncate">
                {booking.reservationNumber || booking.id}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <PaymentBadge paymentStatus={booking.paymentStatus} />
            <StatusBadge status={booking.status} />
          </div>

          {refreshing ? (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Checking latest payment details...
            </div>
          ) : paid ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              <p className="font-semibold">
                Refund {amountLabel} to the customer manually.
              </p>
              <p className="mt-1">
                Note this amount down before continuing. The booking record
                can't be recovered after deletion.
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              This booking is unpaid, so no refund is needed.
            </p>
          )}

          <p className="text-xs text-gray-500">
            All tee-time slots held by this booking will be released. This can't
            be undone.
          </p>

          {error && (
            <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex gap-2.5 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 h-11 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-100 transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className="flex-1 h-11 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              {paid ? "Refund noted, delete" : "Delete"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
/* ---------- booking detail modal ---------- */

function BookingDetailModal({ booking, onClose }) {
  const courses = booking.selectedCourses || [];
  const dates = booking.roundDates || [];
  const times = booking.roundTimes || [];
  const roster = getGolferRoster(booking);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h3 className="text-lg font-bold text-[#0B2421]">Booking Details</h3>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="flex items-center gap-3 pb-5 border-b border-gray-100">
            <Avatar name={booking.customerName} />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[#0B2421]">
                {booking.customerName || "Guest"}
              </p>
              <p className="text-xs text-gray-400">
                {booking.customerEmail || "-"}
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              <PaymentBadge paymentStatus={booking.paymentStatus} />
              <StatusBadge status={booking.status} />
            </div>
          </div>

          {/* Date of Booking / Time of Booking / Reservation No. */}
          <div className="grid grid-cols-3 gap-3 bg-gray-50 rounded-xl p-3">
            <div>
              <p className="text-[10px] font-semibold uppercase text-gray-400 mb-1">
                Date of Booking
              </p>
              <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                {formatTimestamp(booking.createdAtServer)}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase text-gray-400 mb-1">
                Time of Booking
              </p>
              <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                {formatTimeOnly(booking.createdAtServer)}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase text-gray-400 mb-1">
                Reservation No.
              </p>
              <p className="text-sm font-semibold text-gray-700 font-mono truncate">
                {booking.reservationNumber || "-"}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-[10px] font-semibold uppercase text-gray-400 mb-1">
                Players
              </p>
              <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-gray-400" />
                {booking.numPlayers ?? "-"}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-[10px] font-semibold uppercase text-gray-400 mb-1">
                Total
              </p>
              <p className="text-sm font-bold text-[#0B2421]">
                {booking.grandTotal != null ? `SGD ${booking.grandTotal}` : "-"}
              </p>
            </div>
          </div>

          {roster.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase text-gray-400 mb-2">
                Golfers
              </p>
              <div className="space-y-2">
                {roster.map((g, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-gray-100 p-3"
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-sm font-semibold text-[#0B2421] truncate">
                        {g.name || "-"}
                      </p>
                      {g.isLead && (
                        <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-800 bg-emerald-100 rounded-full px-2 py-0.5 flex-shrink-0">
                          Lead
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      {g.passportNumber && (
                        <span className="flex items-center gap-1">
                          <IdCard className="h-3 w-3 flex-shrink-0" />
                          {g.passportNumber}
                        </span>
                      )}
                      {g.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3 flex-shrink-0" />
                          {g.phone}
                        </span>
                      )}
                      {g.email && (
                        <span className="flex items-center gap-1 truncate">
                          <Mail className="h-3 w-3 flex-shrink-0" />
                          {g.email}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {booking.reservationNumber && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400">Reservation</span>
              <span className="font-mono font-semibold text-gray-600">
                {booking.reservationNumber}
              </span>
            </div>
          )}

          {courses.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase text-gray-400 mb-2">
                Rounds
              </p>
              <div className="space-y-2">
                {courses.map((course, i) => (
                  <div
                    key={`${course.id || course.name}-${i}`}
                    className="flex items-center gap-3 rounded-xl border border-gray-100 p-3"
                  >
                    {course.image && (
                      <img
                        src={course.image}
                        alt=""
                        className="w-11 h-11 rounded-lg object-cover flex-shrink-0"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-[#0B2421] truncate">
                        {course.name || `Round ${i + 1}`}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                        {formatDateStr(dates[i]) && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDateStr(dates[i])}
                          </span>
                        )}
                        {times[i] && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {times[i]}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {booking.selectedAccommodation && (
            <div>
              <p className="text-xs font-semibold uppercase text-gray-400 mb-2">
                Accommodation
              </p>
              <div className="flex items-center gap-3 rounded-xl border border-gray-100 p-3">
                {booking.selectedAccommodation.image && (
                  <img
                    src={booking.selectedAccommodation.image}
                    alt=""
                    className="w-11 h-11 rounded-lg object-cover flex-shrink-0"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#0B2421] truncate">
                    {booking.selectedAccommodation.title}
                  </p>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                    {formatDateStr(booking.checkInDate) && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDateStr(booking.checkInDate)} -{" "}
                        {formatDateStr(booking.checkOutDate)}
                      </span>
                    )}
                    {(() => {
                      const rooms = booking.numRooms || booking.numPlayers || 1;
                      return (
                        <span>
                          {rooms} room{rooms !== 1 ? "s" : ""}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {booking.selectedFerry && (
            <div>
              <p className="text-xs font-semibold uppercase text-gray-400 mb-2">
                Ferry
              </p>
              <div className="flex items-center gap-3 rounded-xl border border-gray-100 p-3">
                {booking.selectedFerry.image && (
                  <img
                    src={booking.selectedFerry.image}
                    alt=""
                    className="w-11 h-11 rounded-lg object-cover flex-shrink-0"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#0B2421] truncate">
                    {booking.selectedFerry.title}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {booking.selectedFerry.originport} ⇄{" "}
                    {booking.selectedFerry.destination}
                  </p>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                    {booking.selectedFerryTime && (
                      <span>In: {booking.selectedFerryTime}</span>
                    )}
                    {booking.selectedOutboundFerryTime && (
                      <span>Out: {booking.selectedOutboundFerryTime}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {booking.selectedTransport && (
            <div>
              <p className="text-xs font-semibold uppercase text-gray-400 mb-2">
                Land Transport
              </p>
              <div className="flex items-center gap-3 rounded-xl border border-gray-100 p-3">
                {booking.selectedTransport.image && (
                  <img
                    src={booking.selectedTransport.image}
                    alt=""
                    className="w-11 h-11 rounded-lg object-cover flex-shrink-0"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#0B2421] truncate">
                    {booking.selectedTransport.title}
                    {booking.selectedTransport.type
                      ? ` · ${booking.selectedTransport.type}`
                      : ""}
                  </p>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                    {booking.selectedTransportTime && (
                      <span>In: {booking.selectedTransportTime}</span>
                    )}
                    {booking.selectedOutboundTransportTime && (
                      <span>Out: {booking.selectedOutboundTransportTime}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- invoice modal ---------- */

function InvoiceModal({
  loading,
  error,
  invoice,
  onClose,
  onViewPass,
  viewingPass,
  onDownloadPass,
  generatingPass,
  passError,
  onEmailPass,
  emailingPass,
  emailError,
  emailSentAt,
}) {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 print:bg-white print:relative print:p-0"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto print:max-h-none print:rounded-none print:shadow-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 pb-0 print:hidden">
          <h3 className="text-lg font-bold text-[#0B2421]">Invoice</h3>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400 gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Generating invoice...
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          ) : invoice ? (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-xs uppercase text-gray-400">Invoice</p>
                  <p className="text-lg font-bold text-[#0B2421]">
                    {invoice.invoiceNumber}
                  </p>
                </div>
                <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 capitalize">
                  {invoice.status}
                </span>
              </div>

              <div className="mb-6">
                <p className="text-xs font-semibold uppercase text-gray-400 mb-1">
                  Bill To
                </p>
                <p className="text-sm text-gray-700">
                  {invoice.customerName || "-"}
                </p>
                <p className="text-xs text-gray-400">
                  {invoice.customerEmail || ""}
                </p>
              </div>

              <div className="mb-6 space-y-2">
                {(invoice.selectedCourses || []).map((course, i) => (
                  <div
                    key={`${course.id || course.name}-${i}`}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-gray-600">
                      {course.name || `Round ${i + 1}`}
                      {invoice.roundDates?.[i]
                        ? ` — ${formatDateStr(invoice.roundDates[i])}`
                        : ""}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-100 pt-4 flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  {invoice.numPlayers} player(s)
                </span>
                <span className="text-xl font-bold text-[#0B2421]">
                  ${invoice.total}
                </span>
              </div>

              {passError && (
                <div className="mt-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{passError}</span>
                </div>
              )}

              <button
                type="button"
                onClick={onViewPass}
                disabled={viewingPass}
                className="w-full mt-6 h-11 rounded-xl border border-[#0B2421] text-[#0B2421] font-semibold hover:bg-[#0B2421]/5 transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {viewingPass ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Opening Voucher...
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4" />
                    View Golf Getaway Pass
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={onDownloadPass}
                disabled={generatingPass}
                className="w-full mt-3 h-11 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {generatingPass ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating Pass...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Download Golf Getaway Pass
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={onEmailPass}
                disabled={emailingPass}
                className="w-full mt-3 h-11 rounded-xl border border-[#0B2421] text-[#0B2421] font-semibold hover:bg-[#0B2421]/5 transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {emailingPass ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4" />
                    Email Pass to Customer
                  </>
                )}
              </button>

              {emailError && (
                <div className="mt-3 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{emailError}</span>
                </div>
              )}

              {emailSentAt && !emailError && (
                <p className="mt-3 text-xs text-gray-400 text-center">
                  Last sent {formatTimestamp(emailSentAt)} at{" "}
                  {formatTimeOnly(emailSentAt)}
                </p>
              )}

              {invoice.selectedAccommodation && (
                <div className="flex items-center justify-between text-sm mb-1 mt-6">
                  <span className="text-gray-600">
                    {invoice.selectedAccommodation.title}
                    {invoice.checkInDate
                      ? ` — ${formatDateStr(invoice.checkInDate)}`
                      : ""}
                    {` · ${invoice.numRooms || invoice.numPlayers || 1} room${(invoice.numRooms || invoice.numPlayers || 1) !== 1 ? "s" : ""}`}
                  </span>
                  <span className="text-gray-700 font-medium">
                    {invoice.selectedAccommodation.price != null
                      ? `SGD ${invoice.selectedAccommodation.price}`
                      : ""}
                  </span>
                </div>
              )}
              {invoice.selectedFerry && (
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">
                    {invoice.selectedFerry.title} (round trip)
                  </span>
                  <span className="text-gray-700 font-medium">
                    {invoice.selectedFerry.price != null
                      ? `SGD ${invoice.selectedFerry.price}`
                      : ""}
                  </span>
                </div>
              )}
              {invoice.selectedTransport && (
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">
                    {invoice.selectedTransport.title}
                  </span>
                  <span className="text-gray-700 font-medium">
                    {invoice.selectedTransport.price != null
                      ? `SGD ${invoice.selectedTransport.price}`
                      : ""}
                  </span>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */

function formatTimestamp(ts) {
  if (!ts) return "-";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(date.getTime())) return "-";
  return format(date, "dd MMM yyyy");
}

function formatTimeOnly(ts) {
  if (!ts) return "-";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDateStr(dateVal) {
  if (!dateVal) return "";
  try {
    const date = dateVal?.toDate ? dateVal.toDate() : new Date(dateVal);
    if (Number.isNaN(date.getTime())) return "";
    return format(date, "dd MMM yyyy");
  } catch {
    return "";
  }
}
